import { FilterMatchMode, FilterOperator } from "primevue/api/";
import { timeParse } from "d3";

export class AnalyticsManager {
  constructor(processData, assetType = "commodity") {
    this.processData = processData;
    this.isComm = assetType === "commodity";
  }

  /* Define empty totals for all Analytics */
  defineTotals() {
    return [{
      date: "...",
      budgetQ: 0,
      forecastQ: 0,
      ratioQ: 0,
      fixedQ: 0,
      hedgedQ: 0,
      budgetQNorm: 0,
      forecastQNorm: 0,
      ratioQNorm: 0,
      fixedQNorm: 0,
      hedgedQNorm: 0,
      ratioQPerc: 0,
      fixedQPerc: 0,
      hedgedQPerc: 0,
      budgetP: 0,
      fixedP: 0,
      marketP: 0,
      netP: 0,
      fixedPPerc: 0,
      marketPPerc: 0,
      netPPerc: 0,
      budgetPOther: 0,
      fixedPOther: 0,
      marketPOther: 0,
      netPOther: 0,
      fixedPOtherPerc: 0,
      marketPOtherPerc: 0,
      netPOtherPerc: 0,
      budgetPRatio: 0,
      fixedPRatio: 0,
      marketPRatio: 0,
      netPRatio: 0,
      fixedPRatioPerc: 0,
      marketPRatioPerc: 0,
      netPRatioPerc: 0,
      budgetPFut: 0,
      fixedPFut: 0,
      marketPFut: 0,
      netPFut: 0,
      fixedPFutPerc: 0,
      marketPFutPerc: 0,
      netPFutPerc: 0,
      budgetPFutRatio: 0,
      fixedPFutRatio: 0,
      marketPFutRatio: 0,
      netPFutRatio: 0,
      fixedPFutRatioPerc: 0,
      marketPFutRatioPerc: 0,
      netPFutRatioPerc: 0
    }];
  }

  /* Define dropdowns for all Analytics */
  defineDDowns() {
    return {
      businessDDown: [],
      commoditiesDDown: [],
      contractDDown: [],
      counterPDDown: [],
      countryDDown: [],
      expiredDDown: [],
      expoCodeDDown: [],
      expoTypeDDown: [],
      fixStatusDDown: [],
      futuresDDown: [],
      originDDown: [],
      plantDDown: [],
      portfoliosDDown: [],
      pricingMethodDDown: [],
      strategiesDDown: [],
      supplierDDown: []
    };
  }

  /* Define empty valuation table for all Analytics */
  defineEmptyValTable() {
    const tableEntries = [
      { name: "Expired hedges" },
      // { name: "NEW HEDGES" },
      // { name: "OTHER HEDGES" },
      { name: "Open positions" }
    ];
    const isQty = this.isComm
      ? { txt_quantity: "Quantity" }
      : { txt_notional: "Notional" };

    const commonTotals = {
      quantity: 0,
      txt_delta: "Delta",
      delta: 0,
      txt_derivatives: "Derivatives",
      n_derivatives: 0,
      txt_strats: "Strategies",
      n_strats: 0,
      txt_value: "Valuation on MM DD, YYY",
      hedge_value: 0,
      txt_value_initial: "Valuation on MM DD, YYY",
      hedge_value_initial: 0,
      ...isQty
    };
    return tableEntries.map(entry => ({
      ...entry,
      totals: [commonTotals],
      details: [],
      show: false,
      selected: []
    }));
  }

  /* Define empty Analytics Variable */
  static defaultAnalytics(customFilters) {
    const analytics = new AnalyticsManager();
    return {
      companies: [{ id: 0, company_name: "N.A." }],
      loading: {
        accounting: false,
        budget: false,
        expoFuture: false,
        expoPast: false,
        hedgesFuture: false,
        hedgesPast: false,
        histCandles: false,
        management: false,
        pastCalcs: false,
        portCalcs: false,
        program: false,
        spot: false,
        strategies: false,
        derivatives: false,
        topographyCalcs: false,
        topographyCalcsRates: false,
        valuationFinalSpot: false,
        valuationInitialSpot: false
      },
      rawData: {
        budget: [],
        dataFilters: analytics.defineFilters(customFilters),
        dDowns: analytics.defineDDowns(),
        derivatives: [],
        existLCall: false,
        existLFwd: false,
        existLPut: false,
        existSCall: false,
        existSFwd: false,
        existSPut: false,
        expoFuture: [],
        expoPast: [],
        filteredData: [],
        hedgesFuture: [],
        hedgesPast: [],
        historicalData: [],
        historicalOHLC: {},
        mergedExpoHedges: [],
        nestedData: [],
        prevSpot: null,
        program: [],
        render: true,
        spot: null,
        strategies: [],
        toBeFetched: true,
        toBeFetchedHidden: false,
        totals: analytics.defineTotals(),
        years: {},
        zoomedData: [],
        zoomedDerivatives: []
      },
      underlyingSpecs: [],
      userSelections: {
        assets: [
          { product_name: "N.A.", fk_idcurrency: "N.A.", fk_idunit: "N.A." }
        ],
        bucket: "MONTHLY",
        expandSidebar: false,
        initStoreCompPortAsset: null,
        portfolioNums: [],
        portfolios: [],
        selectedAnalysis: "scenarios",
        selectedAnalysisParent: "scenario",
        selectedAsset: [
          {
            product_name: "N.A.",
            fk_idcurrency: "N.A.",
            fk_idunit: "N.A.",
            currency_name: "N.A/N.A"
          }
        ],
        selectedCompanies: [{ id: 0, company_name: "N.A." }],
        selectedPortfolios: [],
        selectedTable: "strategies",
        selectedUnderlying: [{ commodity_name: "N.A." }],
        underlying: [{ commodity_name: "N.A." }],
        thenCandleData: null,
        visibleLeft: false,
        xZoom: []
      },
      management: {
        strategies: [],
        contracts: [],
        date: [],
        clients: [],
        suppliers: []
      },
      valuation: {
        data: analytics.defineEmptyValTable(),
        dDowns: analytics.defineDDowns(),
        finalDate: null,
        finalSpot: null,
        initialDate: null,
        initialSpot: null,
        selectedAsset: [],
        valuated: false
      }
    };
  }

  /* Define filters for all Analytics */
  defineFilters(customFilters) {
    const newFilters = {};
    for (const key in customFilters) {
      newFilters[key] = this[customFilters[key]](null);
    }

    return {
      ...newFilters,
      buy_sell: this.createInConstraint(null),
      client_str: this.createAndConstraint(null),
      comments: this.createAndConstraint(null),
      counterparty: this.createInConstraint(null),
      custom_id: this.createInConstraint(null),
      custom_name: this.createAndConstraint(null),
      date: this.createDateIsConstraint(),
      derivative: this.createInConstraint(null),
      eta: this.createDateIsConstraint(),
      exposure_type: this.createInConstraint(null),
      expired: this.createInConstraint(null),
      business: this.createInConstraint(null),
      fk_id_client: this.createInConstraint(null),
      fk_id_country_origin: this.createInConstraint(null),
      country_of_origin: this.createInConstraint(null),
      fk_id_plant: this.createInConstraint(null),
      plant_str: this.createInConstraint(null),
      fk_id_provider: this.createInConstraint(null),
      freight: this.createAndConstraint(null),
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      hedge_asset: this.createInConstraint(null),
      insurance: this.createAndConstraint(null),
      plant: this.createInConstraint(null),
      portfolio: this.createInConstraint(null),
      purchase_date: this.createDateIsConstraint(),
      quotation: this.createInConstraint(null),
      sale_date: this.createDateIsConstraint(),
      settlement_pnl: this.createAndConstraint(null),
      settlement_price: this.createAndConstraint(null),
      shipping_date: this.createDateIsConstraint(),
      strategy_name: this.createInConstraint(null),
      strike: this.createAndConstraint(null),
      supplier: this.createInConstraint(null),
      tariffs: this.createAndConstraint(null)
    };
  }

  /**
   * Create dropdowns for Valuation Filters
   * @param {Array} dDownData - Array of data objects used to populate and filter dropdown options
   * @returns {Object} dDowns - Object containing key-value pairs where keys are dropdown identifiers
   *                            and values are arrays of dropdown options
   */
  createDDowns(dDownData) {
    // Define categories for dropdowns with their corresponding data keys and types
    const categories = [
      { key: "businessDDown", type: "business" },
      { key: "clientDDown", type: "client_str" },
      { key: "commoditiesDDown", type: "end_product_str" },
      { key: "contractDDown", type: "contract_month_year", hasValue: true },
      { key: "counterPDDown", type: "counterparty" },
      { key: "countryDDown", type: "country_of_destination" }, // { key: "expiredDDown", value: ["Active", "Expired"] },
      { key: "expoCodeDDown", type: "custom_id" },
      { key: "expoTypeDDown", type: "exposure_type" }, // { key: "fixStatusDDown", value: ["Fixed", "Not Fixed"] },
      { key: "futuresDDown", type: "commodity_hedge" },
      { key: "originDDown", type: "country_of_origin" },
      { key: "plantDDown", type: "plant_str" },
      { key: "portfoliosDDown", type: "portfolio" },
      { key: "pricingMethodDDown", type: "pricing_method" },
      { key: "strategiesDDown", type: "strategy_name" },
      { key: "supplierDDown", type: "supplier" }
    ];
    // Initialize object to store dropdown data
    let dDowns = {};
    // Iterate through each category to populate dropdown data
    categories.forEach(category => {
      if (category.type) {
        // Generate dropdown options using valuationDDowns method for categories with a type
        dDowns[category.key] = this.valuationDDowns(
          dDownData,
          category.type,
          category?.hasValue
        );
      } else {
        // Directly assign predefined values for categories without a type
        dDowns[category.key] = category.value;
      }
    });
    // Return the populated dropdowns object
    return dDowns;
  }

  /**
   * Create dropdown options for a specific field used in valuation filters
   * @param {Array} allData - Array of data objects containing details to filter
   * @param {string} fieldName - The field name to extract and filter for dropdown options
   * @param {boolean} [futSort=false] - Indicates if the field represents futures contract dates (MMMYY format) for custom sorting
   * @returns {Array} dDown - Sorted array of unique, non-null, non-empty values for the specified field
   */
  valuationDDowns(allData, fieldName, futSort = false) {
    // Extract field values from nested details in allData
    let dDown = allData.flatMap(data => data.details.map(d => d[fieldName]));
    // Filter out null/undefined and empty string values, then keep only unique values
    dDown = dDown
      .filter(d => d != null && d !== "")
      .filter(this.processData.onlyUnique);
    // Sort the dropdown options
    if (futSort) {
      // Sort futures contract dates (MMMYY) chronologically using timeParse
      dDown.sort((a, b) => +timeParse("%m%Y")(a) - +timeParse("%m%Y")(b));
    } else {
      // Default alphabetical/numerical sort for non-futures fields
      dDown.sort();
    }
    // Return the sorted array of dropdown options
    return dDown;
  }

  createAndConstraint(value) {
    return {
      operator: FilterOperator.AND,
      constraints: [{ value, matchMode: FilterMatchMode.EQUALS }]
    };
  }

  createInConstraint(value) {
    return { value, matchMode: FilterMatchMode.IN };
  }

  createDateIsConstraint() {
    return { value: null, matchMode: FilterMatchMode.DATE_IS };
  }

  createStartsWithConstraint() {
    return { value: null, matchMode: FilterMatchMode.STARTS_WITH };
  }
}
